import { useEffect, useId, useRef } from 'react';
import { HelperText, Input, InputRow, Textarea } from '@/ui';
import { StatusDotPill } from '@/shared';
import { useCan } from '@/hooks';
import { useAuth } from '@/app/AuthContext.jsx';
import { formatChileanDate } from '@/lib/dateUtils';
import ComunaCombo from '@/pages/diligencias/ComunaCombo';
import { STAGE_LABEL } from './channelDiligenceCopy';
import { chileToday } from './channelDiligenceForm';
import { useChannelDiligence } from './useChannelDiligence';
import '@/pages/diligencias/NuevaDiligenciaForm.css';
import './ChannelPayinPanel.css';
import './ChannelDiligencePanel.css';

function recipientLine(r) {
  return [r.address, r.addressDetails, r.commune].filter(Boolean).join(', ');
}

// The live diligence of the pedido (or the one just created): its stage and one
// line per recipient (the created one has no list, only its address).
function DiligenceSummary({ diligence }) {
  const recipients = diligence.recipients ?? [];
  return (
    <>
      <StatusDotPill
        status="warn"
        label={STAGE_LABEL[diligence.stage] ?? diligence.stage ?? 'Viva'}
      />
      {diligence.created === false && (
        <p className="pli-detail__note">La diligencia ya existía, se retomó.</p>
      )}
      {diligence.deadline && (
        <p className="pli-detail__note">
          Plazo: {formatChileanDate(String(diligence.deadline).slice(0, 10))}
        </p>
      )}
      {recipients.length > 0 ? (
        <ol className="pli-dili__recipients" aria-label="Destinatarios">
          {recipients.map((r, i) => (
            <li key={`${i}-${r.address ?? ''}`}>{recipientLine(r)}</li>
          ))}
        </ol>
      ) : (
        <p className="pli-detail__note">
          {[diligence.address, diligence.commune].filter(Boolean).join(', ') ||
            'Sin destinatarios cargados.'}
        </p>
      )}
    </>
  );
}

function AddressRow({ row, err = {}, show, busy, onRow }) {
  const e = (field) => (show ? err[field] : undefined);
  const field = (name, patch) => onRow(row.key, { [name]: patch });
  return (
    <fieldset className="pli-dili__row">
      <legend className="pli-dili__legend">Dirección</legend>
      <InputRow label="Calle y número" error={e('address') ?? false}>
        <Input
          aria-label="Calle y número de la dirección"
          value={row.address}
          maxLength={300}
          disabled={busy}
          onChange={(ev) => field('address', ev.target.value)}
        />
        {e('address') && <HelperText kind="err">{e('address')}</HelperText>}
      </InputRow>
      <InputRow label="Detalle (opcional)" error={e('details') ?? false}>
        <Input
          aria-label="Detalle de la dirección"
          placeholder="Oficina, departamento"
          value={row.details}
          maxLength={300}
          disabled={busy}
          onChange={(ev) => field('details', ev.target.value)}
        />
        {e('details') && <HelperText kind="err">{e('details')}</HelperText>}
      </InputRow>
      <InputRow label="Comuna · Región" error={e('comuna') ?? false}>
        <ComunaCombo
          aria-label="Comuna y región de la dirección"
          value={row.comuna}
          disabled={busy}
          error={Boolean(e('comuna'))}
          onChange={(v) => field('comuna', v)}
        />
        {e('comuna') && <HelperText kind="err">{e('comuna')}</HelperText>}
      </InputRow>
    </fieldset>
  );
}

// Presentational: every state is a prop of `view`, so Storybook and the tests
// drive it without the API. `view` is what useChannelDiligence returns.
export function ChannelDiligenceView({ view }) {
  const headingId = useId();
  const headingRef = useRef(null);
  const formRef = useRef(null);
  const {
    loading,
    error,
    refreshing,
    linkedCase,
    liveTask,
    created,
    createdCount = 0,
    invalidCount = 0,
    rows,
    deadline,
    notes,
    submitted,
    errors,
    busy,
  } = view;

  // After the create succeeds, focus moves to the title so the result is
  // announced and a keyboard user lands on it.
  useEffect(() => {
    if (createdCount > 0) headingRef.current?.focus();
  }, [createdCount]);

  // A submit the client rejects moves focus to the first invalid field, so a
  // keyboard or screen reader user does not stay on the button at the bottom.
  useEffect(() => {
    if (invalidCount === 0) return;
    formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [invalidCount]);

  const diligence = liveTask ?? created;

  let body;
  if (loading) {
    body = <p className="pli-detail__note">Cargando…</p>;
  } else if (error) {
    body = (
      <>
        <p className="pli-detail__note" role="alert">
          No se pudo cargar el pedido.
        </p>
        <div className="pli-charge__actions">
          <button
            type="button"
            className="plp-morebtn"
            disabled={refreshing}
            onClick={view.onRefresh}
          >
            {refreshing ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </>
    );
  } else if (diligence) {
    body = <DiligenceSummary diligence={diligence} />;
  } else if (!linkedCase) {
    body = (
      <p className="pli-detail__note">
        Este pedido todavía no tiene una causa vinculada, así que no se pueden
        cargar direcciones.
      </p>
    );
  } else {
    body = (
      <form
        ref={formRef}
        className="pli-dili__form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          view.onSubmit();
        }}
      >
        <p className="pli-detail__note">
          Causa {linkedCase.rol ?? 'vinculada'}. La diligencia va a una
          dirección.
        </p>
        <div className="pli-dili__common">
          <InputRow
            label="Plazo"
            error={(submitted && errors.deadline) || false}
          >
            <Input
              variant="mono"
              type="date"
              min={chileToday()}
              value={deadline}
              disabled={busy}
              onChange={(e) => view.onDeadline(e.target.value)}
            />
            {submitted && errors.deadline && (
              <HelperText kind="err">{errors.deadline}</HelperText>
            )}
          </InputRow>
          <InputRow label="Notas (opcional)">
            <Textarea
              rows={2}
              placeholder="Disponibilidad, referencias para el receptor"
              value={notes}
              disabled={busy}
              onChange={(e) => view.onNotes(e.target.value)}
            />
          </InputRow>
        </div>
        {rows.map((row) => (
          <AddressRow
            key={row.key}
            row={row}
            err={errors.rows[row.key]}
            show={submitted}
            busy={busy}
            onRow={view.onRow}
          />
        ))}
        <div className="pli-charge__actions">
          <button type="submit" className="plp-rowbtn" disabled={busy}>
            {busy ? 'Creando…' : 'Crear diligencia'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <section
      className="pli-charge pli-dili"
      aria-labelledby={headingId}
      aria-busy={busy || loading ? 'true' : undefined}
    >
      <div className="pli-charge__head">
        <h3
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="plp-detail__h"
        >
          {diligence ? 'Diligencia' : 'Direcciones'}
        </h3>
      </div>
      <div aria-live="polite">{body}</div>
    </section>
  );
}

// "Cargar direcciones" of an accepted pedido. Only `channel-intake:accept`,
// the same `authorize` as the POST (the API's 403 stays the source of truth).
export default function ChannelDiligencePanel(props) {
  // Keyed by pedido: nothing typed for one pedido carries over to the next.
  return <ChannelDiligencePanelFor key={props.intakeId} {...props} />;
}

function ChannelDiligencePanelFor({ intakeId }) {
  const allowed = useCan('channel-intake:accept');
  const organizationId = useAuth()?.organizationId ?? null;
  const view = useChannelDiligence({
    intakeId,
    organizationId,
    enabled: allowed,
  });
  if (!allowed) return null;
  return <ChannelDiligenceView view={view} />;
}
